/**
 * search.module.css — the Search screen and the drawer index.
 * `docs/design-direction.md` §8.3.
 *
 * WHAT IS **NOT** HERE, ON PURPOSE
 *
 * The printed marks already exist as global recipes in `styles/texture.css`
 * (`.rule`, `.halftone`, `.stamp`, `.ruler`) and the type tiers in `styles/type.css`
 * (`.t-display`, `.t-mono`, `.t-20`, `.t-13`, `.t-11`). The two components attach
 * those names verbatim; this module adds only what the screen owns — the chalk line,
 * the recent-query stamps, the group rules, the 28px index rows and the two
 * animations. Re-declaring a global recipe here would fork it, and the font-failure
 * degradation in `fonts.css` hangs off the global type class names, so any text that
 * carried a local class instead of `t-mono` would silently opt out of §4.4.
 *
 * THE RULES EVERY DECLARATION BELOW OBEYS
 *
 *  1. **Semantic aliases only** (§3.2). No `--ink-*`, `--paper-*`, `--stock-*`,
 *     `--warm-*` or `--cool` appears in this file.
 *  2. **Radii are `var(--r-cut)` (0) or `var(--r-round)` (999px)** — nothing between
 *     (§5.3).
 *  3. **Separation is a 1px printed rule, never a shadow** (§5.5).
 *  4. **Every length is a token**, or an expression over the scale where §8.3 names a
 *     value the scale does not carry (the 48px bar, the 28px index row).
 *  5. **Only `transform`, `opacity` and `clip-path` animate** (§7.1, §10.2.3). That is
 *     why the long-press strike-through is a 1px rule drawing in from the left on
 *     `scaleX` rather than the `stroke-dasharray` sweep §8.3 sketches: a dash offset
 *     is not on the licensed list, and "a 1px rule drawing in from the left" is the
 *     motion this system already uses everywhere else.
 *  6. **Green does not exist** (§3.1, §12.1) — no hue in 70–170° appears here.
 *
 * TWO DELIBERATE DEVIATIONS, BOTH FORCED BY THE CONTRACT ITSELF
 *
 *  · **The recent-query marks do not use the global `.stamp` recipe.** That recipe
 *    carries `mix-blend-mode: screen` and a grain dropout mask. A row of five recent
 *    queries would put five blended elements on one screen against a budget of three
 *    (§6.13), and the mask is banned on anything the user must read at this size
 *    (§12.11) — which a user's own Cyrillic query is. So `.stampish` reproduces the
 *    1px box, the mono caps and the alternating tilt of §8.3 with neither.
 *  · **The 28px index row is under the 44px minimum target** (§5.6). §8.3 asks for a
 *    dense 28px row *by name* and calls the drawer index the best screen in the app;
 *    the density is the point. The row is therefore 28px tall and spans a whole
 *    column — a wide target, and a deliberate, documented trade rather than an
 *    oversight. No hit expander is added: at 28px an expander would overlap its
 *    neighbours and steal their taps, which is worse than a small target.
 *
 * `prefers-reduced-motion` needs no block here: `motion.css` collapses every
 * animation to one 1ms iteration, and both keyframes below are written so the
 * element's resting style is the finished style — the results simply appear in
 * register and the strike is simply drawn.
 */

/* ────────────────────────────────────────────────────────────────────────────────
   Screen shell.
   ──────────────────────────────────────────────────────────────────────────────── */

.screen {
  display: grid;
  grid-template-columns: 1fr;
  align-content: start;
  gap: var(--s4);
}

/*
 * The 48px sticky bar of the screen contract (§8): `--bg-panel`, a 1px
 * `--border-strong` bottom edge, a halftone underlay, nothing centred. It is taller
 * than 48px because the chalk line lives inside it, so the contract's 48px is the
 * minimum rather than the height.
 */
.bar {
  position: sticky;
  inset-block-start: 0;
  z-index: var(--z-sticky);
  display: grid;
  gap: var(--s2);
  min-block-size: calc(var(--drop) + var(--s1));
  padding: var(--s3) var(--gutter) var(--s2);
  background: var(--bg-panel);
  border-block-end: 1px solid var(--border-strong);
}

/* ────────────────────────────────────────────────────────────────────────────────
   §8.3 — the chalk line. NOT a search box: no ground, no frame, one underline that
   thickens to 2px `--accent` on focus. The caret is warm ink, from `reset.css`.
   ──────────────────────────────────────────────────────────────────────────────── */

.line {
  position: relative;
  display: block;
  min-block-size: var(--drop);
  padding-block-end: var(--s2);
  border-block-end: 1px solid var(--border);
}

/*
 * Focus thickens the line to 2px `--accent` (§5.4's active tier) as an overlay rather
 * than as a second border width: a border that grows by 1px on focus moves every row
 * beneath it by 1px, and the whole point of this system's states is that nothing
 * jumps. It draws in from the left, like every other rule here.
 */
.line::after {
  content: '';
  position: absolute;
  inset-inline: 0;
  inset-block-end: -1px;
  block-size: 2px;
  background: var(--border-active);
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform var(--t16) var(--ease-cut);
  pointer-events: none;
}

.line:focus-within::after {
  transform: scaleX(1);
}

.input {
  display: block;
  inline-size: 100%;
  min-block-size: var(--drop);
  padding: 0;
  color: var(--text);
  background: none;
  border: 0;
  border-radius: var(--r-cut);
}

/*
 * NO BLUE RECTANGLE ROUND THE CHALK LINE — the operator's report of 2026-09-05.
 *
 * `reset.css`'s §5.4 focus tier draws `2px solid var(--focus)` (the cool ink) on
 * `:focus-visible`, and a browser treats a TEXT INPUT as focus-visible even when it was
 * reached by a tap — so tapping the search field drew a blue box round the whole field
 * inside Telegram. That ring is a duplicate here and only here: `.line:focus-within`
 * already thickens the chalk line to 2px `--border-active` over `--t16`, which is the
 * same §5.4 tier expressed in this screen's own language (§8.3: "not a search box —
 * one underline"). The ring is REPLACED, never removed: focus is still visible, it is
 * just drawn as the line the design already draws.
 *
 * Scoped to this element. The global ban in `reset.css` on removing the ring outright
 * stands for every control that has no focus affordance of its own.
 */
.input:focus-visible {
  outline: none;
}

/*
 * A real element, not `::placeholder`. Three reasons, in order of weight: §8.3 asks
 * for a **mono caps** placeholder while the input itself is Display 800, and a
 * pseudo-element cannot carry the `t-mono` class that `fonts.css` §4.4 degrades;
 * a real element is inspectable, so "the placeholder is mono, and it reads
 * `ЧТО ИГРАЕМ?`" is a fact a test can assert rather than a hope; and `::placeholder`
 * inherits the input's `text-transform`, which would shout a Cyrillic string in the
 * one tier §4.3 forbids from shouting.
 *
 * It is `aria-hidden` and `pointer-events: none`: the input's accessible name comes
 * from its own `<label>`, never from a placeholder (§8.3, §13's a11y checklist).
 */
.placeholder {
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  display: flex;
  align-items: center;
  min-block-size: var(--drop);
  color: var(--text-3);
  pointer-events: none;
}

/* The mono readout beside the line: the count and the echoed `backend` (§6.5). Latin
   and numerals only, so the caps tier is licensed here (§4.3). */
.readout {
  display: flex;
  gap: var(--s3);
  align-items: baseline;
  justify-content: space-between;
  color: var(--text-3);
}

/* ────────────────────────────────────────────────────────────────────────────────
   §8.3 — recent queries as rubber stamps, rotated alternately −2.5° / −4.5° so the
   row reads hand-stamped. See the header for why this is not the `.stamp` recipe.
   ──────────────────────────────────────────────────────────────────────────────── */

.recent {
  display: flex;
  flex-wrap: wrap;
  gap: var(--s3) var(--s4);
  align-items: center;
  padding-inline: var(--gutter);
}

.stampish {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-block-size: var(--drop);
  padding-inline: var(--s3);
  color: var(--accent-quiet);
  background: none;
  border: 1px solid currentcolor;
  border-radius: var(--r-cut);
  transform: rotate(var(--tilt, -2.5deg));
}

.stampish:active {
  background: var(--bg-pressed);
}

/*
 * The long-press strike. A 1px rule drawn across the mark from the left over `--t16`
 * — the same gesture as every other rule in the system — after which the screen
 * removes the query. `transform` only (§10.2.3).
 */
.strike {
  position: absolute;
  inset-inline: var(--s2);
  inset-block-start: 50%;
  block-size: 1px;
  background: currentcolor;
  transform: scaleX(1);
  transform-origin: left center;
  animation: bl-search-strike var(--t16) var(--ease-cut) 1;
  pointer-events: none;
}

@keyframes bl-search-strike {
  from {
    transform: scaleX(0);
  }

  to {
    transform: scaleX(1);
  }
}

.striking {
  color: var(--text-3);
}

/* ────────────────────────────────────────────────────────────────────────────────
   §8.3 — results grouped under mono caps rules with counts.
   ──────────────────────────────────────────────────────────────────────────────── */

.group {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
}

.groupHead {
  display: flex;
  gap: var(--s3);
  align-items: baseline;
  justify-content: space-between;
  min-block-size: var(--s8);
  padding: var(--s3) var(--gutter) var(--s2);
  color: var(--text-2);
}

.groupCount {
  color: var(--text-3);
}

/*
 * The 40ms out-of-register arrival of §8.3: each result lands 2px out of register and
 * resolves — crate-digging, one sleeve flipped at a time. `--i` is written by the
 * screen and is capped at 12 rows, exactly as §7.2's ledger stagger is; past that the
 * rows appear at once.
 */
.stagger {
  animation: bl-search-register var(--t16) var(--ease-cut) 1 both;
  animation-delay: calc(var(--i, 0) * 40ms);
}

@keyframes bl-search-register {
  from {
    opacity: 0;
    transform: translateX(2px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/*
 * A superseded query's results stay on screen while the next answer is in flight.
 * They dim; they are never replaced by a loading state, because a result the user can
 * already read is more useful than the news that we are looking again (§8.8).
 */
.stale {
  opacity: 0.55;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The 28px index row — the drawer index (§8.3) and the artist / tag / year result
   rows, which are the same object. Mono `--fs-13`, right-aligned count, hairline
   under each. See the header for the target-size trade.
   ──────────────────────────────────────────────────────────────────────────────── */

.row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--s3);
  align-items: center;
  inline-size: 100%;
  min-block-size: calc(var(--drop) - var(--s4));
  padding-inline: var(--gutter);
  color: var(--text-2);
  text-align: left;
  background: none;
  border: 0;
  border-block-end: 1px solid var(--border);
  border-radius: var(--r-cut);
}

.row:active {
  background: var(--bg-pressed);
}

/* Inside the two-column index the gutter belongs to the state frame, not to the row.
   A modifier rather than a descendant selector: `.index` is a class in
   `states.module.css`, and a CSS module cannot select another module's hashed name. */
.rowFlush {
  padding-inline: 0;
}

.name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Never a fabricated number: the count is rendered only when the API gave one, and
   the element does not exist otherwise (§8.8, §12.6). */
.count {
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

/* ────────────────────────────────────────────────────────────────────────────────
   §8.3 — the matched substring. `--accent-tint` ground **plus** a 1px `--accent`
   underline, because at 13px a colour change alone is not a signal, and colour alone
   is never an acceptable carrier of meaning.
   `mark`'s global recipe (§6.11) is the crooked felt-tip swipe for the set-list
   sheet: a `multiply` blend over cream. Neutralised here — a blended element per
   matched word would blow the three-element budget of §6.13 in the first result row.
   ──────────────────────────────────────────────────────────────────────────────── */

.match {
  padding: 0;
  color: inherit;
  background: var(--accent-tint);
  clip-path: none;
  opacity: 1;
  mix-blend-mode: normal;
}

.matchLine {
  border-block-end: 1px solid var(--accent);
}

/* ────────────────────────────────────────────────────────────────────────────────
   §8.3 — the six stamped-ring tag chips beneath the index. The strip scrolls with
   momentum, hides its scrollbar and fades 16px at both ends, so the row reads as
   continuing rather than ending (§9.7). The mask is licensed: a ring is a mark, not
   13px copy (§12.11).
   ──────────────────────────────────────────────────────────────────────────────── */

.chips {
  display: flex;
  grid-column: 1 / -1;
  gap: var(--s2);
  align-items: center;
  margin-block-start: var(--s6);
  padding-block: var(--s2);
  overflow-x: auto;
  scrollbar-width: none;
  mask-image: linear-gradient(
    90deg,
    transparent,
    #000 var(--s4),
    #000 calc(100% - var(--s4)),
    transparent
  );
  -webkit-mask-image: linear-gradient(
    90deg,
    transparent,
    #000 var(--s4),
    #000 calc(100% - var(--s4)),
    transparent
  );
  -webkit-overflow-scrolling: touch;
}

.chips::-webkit-scrollbar {
  display: none;
}

/* ────────────────────────────────────────────────────────────────────────────────
   Screen-reader-only copy — the input's programmatic label, and the live-region
   announcement of how many results arrived.
   ──────────────────────────────────────────────────────────────────────────────── */

.sr {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/*
 * Cyrillic keeps the mono tier and loses the tracking and the forced caps: tracked
 * uppercase Cyrillic reads as shouting and is banned (§4.3, §12.16). Mirrors `.cyr`
 * in `primitives.module.css` and `states.module.css`.
 */
:global(.t-mono).cyr {
  letter-spacing: 0.02em;
  text-transform: none;
}
